const canvasToBlob = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Не удалось обработать изображение")),
      "image/webp",
      quality
    );
  });

export async function prepareBackground(file: Blob, type = "app") {
  if (!file?.type?.startsWith("image/")) throw new Error("Выберите файл изображения");
  if (file.size > 20 * 1024 * 1024) throw new Error("Исходный файл должен быть меньше 20 МБ");

  let source: ImageBitmap | HTMLImageElement;
  let releaseSource = () => {};
  try {
    source = await createImageBitmap(file, { imageOrientation: "from-image" as ImageOrientation });
    releaseSource = () => { if ("close" in source) source.close(); };
  } catch {
    const objectUrl = URL.createObjectURL(file);
    source = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Формат изображения не поддерживается браузером"));
      image.src = objectUrl;
    });
    releaseSource = () => URL.revokeObjectURL(objectUrl);
  }

  // The profile cover is rendered at roughly 1000×200 even on desktop. A
  // 1600px/1.2MB upload was needlessly expensive on mobile 3G, while the app
  // background also does not need to exceed a normal Full HD viewport.
  const targetWidth = type === "profile" ? 1200 : 1600;
  const targetHeight = type === "profile" ? 270 : 900;
  const sourceWidth = source.width || ("naturalWidth" in source ? source.naturalWidth : 0);
  const sourceHeight = source.height || ("naturalHeight" in source ? source.naturalHeight : 0);
  const targetRatio = targetWidth / targetHeight;
  const sourceRatio = sourceWidth / sourceHeight;
  let cropWidth = sourceWidth;
  let cropHeight = sourceHeight;
  let cropX = 0;
  let cropY = 0;

  if (sourceRatio > targetRatio) {
    cropWidth = sourceHeight * targetRatio;
    cropX = (sourceWidth - cropWidth) / 2;
  } else {
    cropHeight = sourceWidth / targetRatio;
    cropY = (sourceHeight - cropHeight) / 2;
  }

  const scale = Math.min(1, targetWidth / cropWidth, targetHeight / cropHeight);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cropWidth * scale));
  canvas.height = Math.max(1, Math.round(cropHeight * scale));
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) { releaseSource(); throw new Error("Не удалось обработать изображение"); }
  context.drawImage(source, cropX, cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
  releaseSource();

  let quality = type === "profile" ? 0.72 : 0.8;
  let blob = await canvasToBlob(canvas, quality);
  const maxBytes = (type === "profile" ? 180 : 700) * 1024;
  while (blob.size > maxBytes && quality > 0.5) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, quality);
  }
  return blob;
}
